import { TableOfContents } from '@/components/TableOfContents';
import { useDocumentMeta } from '@/hooks/useDocumentMeta';
import { PLAN_LIST, formatPlanLimit, getResourceLimit, isUnlimited, type ResourceKey } from '@logpulse/types/plans';

function Section({ id, title, children }: { id: string; title: string; children: React.ReactNode }) {
  return (
    <section id={id} className="mb-12 scroll-mt-20">
      <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-4 pb-2 border-b border-gray-200 dark:border-gray-800">
        {title}
      </h2>
      <div className="prose-custom">{children}</div>
    </section>
  );
}

function SubSection({ id, title, children }: { id: string; title: string; children: React.ReactNode }) {
  return (
    <div id={id} className="mb-8 scroll-mt-20">
      <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-3">
        {title}
      </h3>
      <div className="prose-custom">{children}</div>
    </div>
  );
}

function P({ children }: { children: React.ReactNode }) {
  return <p className="text-gray-600 dark:text-gray-400 leading-relaxed mb-3">{children}</p>;
}

function CodeBlock({ children, title }: { children: string; title?: string }) {
  return (
    <div className="my-4 rounded-xl border border-gray-200 dark:border-gray-800 overflow-hidden">
      {title && (
        <div className="px-4 py-2 bg-gray-50 dark:bg-gray-900 border-b border-gray-200 dark:border-gray-800 text-xs font-medium text-gray-500 dark:text-gray-400">
          {title}
        </div>
      )}
      <pre className="px-4 py-3 bg-gray-950 text-gray-300 text-sm font-mono overflow-x-auto leading-relaxed">
        <code>{children}</code>
      </pre>
    </div>
  );
}

function Table({ headers, rows }: { headers: string[]; rows: string[][] }) {
  return (
    <div className="my-4 overflow-x-auto rounded-xl border border-gray-200 dark:border-gray-800">
      <table className="w-full text-sm">
        <thead>
          <tr className="bg-gray-50 dark:bg-gray-900">
            {headers.map((h, i) => (
              <th key={i} className="text-left px-4 py-2.5 font-semibold text-gray-700 dark:text-gray-300 border-b border-gray-200 dark:border-gray-800">
                {h}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((row, i) => (
            <tr key={i} className="border-b last:border-b-0 border-gray-100 dark:border-gray-800/50">
              {row.map((cell, j) => (
                <td key={j} className="px-4 py-2.5 text-gray-600 dark:text-gray-400 font-mono text-xs">
                  {cell}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function Callout({ type, children }: { type: 'info' | 'tip' | 'warning'; children: React.ReactNode }) {
  const styles = {
    info: 'border-info-500 bg-info-50 dark:bg-info-500/10 text-info-600 dark:text-info-400',
    tip: 'border-success-500 bg-success-50 dark:bg-success-500/10 text-success-600 dark:text-success-400',
    warning: 'border-warning-500 bg-warning-50 dark:bg-warning-500/10 text-warning-600 dark:text-warning-400',
  };
  const labels = { info: 'Note', tip: 'Tip', warning: 'Warning' };
  return (
    <div className={`my-4 p-4 rounded-xl border-l-4 ${styles[type]}`}>
      <div className="font-semibold text-sm mb-1">{labels[type]}</div>
      <div className="text-sm leading-relaxed">{children}</div>
    </div>
  );
}

export function PlatformLimitsPage() {
  useDocumentMeta({
    title: 'LogPulse Platform Limits: Rate Limits, Quotas & SLOs',
    description: 'Per-plan ingest limits (Free 1 GB/day → Business 50 GB/day), API rate limits, retention windows (7–90 days), and SLO targets. Includes ingest pipeline architecture details.',
  });

  // Org-limit rows derived from plans.ts so the docs can't drift from enforcement.
  const nonStarter = PLAN_LIST.filter((p) => p.id !== 'starter');
  const orgLimitRow = (label: string, resource: ResourceKey): string[] => [
    label,
    ...nonStarter.map((p) => {
      const v = getResourceLimit(p.id, resource);
      return isUnlimited(v) ? 'Unlimited' : String(v);
    }),
    'Unlimited',
  ];

  const toc = [
    { id: 'overview', label: 'Overview' },
    { id: 'slos', label: 'SLOs & Reliability Targets' },
    { id: 'slo-table', label: 'Current Targets', level: 1 },
    { id: 'status-page', label: 'Status Page', level: 1 },
    { id: 'ingest-pipeline', label: 'Ingest Pipeline Architecture' },
    { id: 'delivery-semantics', label: 'Delivery Semantics', level: 1 },
    { id: 'batching', label: 'Batching & Flushing', level: 1 },
    { id: 'failure-handling', label: 'Failure Handling', level: 1 },
    { id: 'backpressure', label: 'Backpressure & Throttling', level: 1 },
    { id: 'rate-limits', label: 'Rate Limits & Quotas' },
    { id: 'api-rate-limits', label: 'API Rate Limits', level: 1 },
    { id: 'daily-quotas', label: 'Daily Ingestion Quotas', level: 1 },
    { id: 'burst-controls', label: 'Burst Controls', level: 1 },
    { id: 'tenant-fairness', label: 'Tenant Fairness', level: 1 },
    { id: 'platform-limits', label: 'Platform Limits' },
    { id: 'event-limits', label: 'Event Size Limits', level: 1 },
    { id: 'query-limits', label: 'Query Limits', level: 1 },
    { id: 'org-limits', label: 'Organization Limits', level: 1 },
    { id: 'cardinality', label: 'Cardinality Guidance' },
    { id: 'cardinality-best-practices', label: 'Best Practices', level: 1 },
  ];

  return (
    <div className="flex gap-8">
      <div className="flex-1 min-w-0">
        <Section id="overview" title="Platform Limits & Quotas">
          <P>
            This page documents LogPulse's service level objectives, rate limits, platform quotas, and
            ingest pipeline architecture. Understanding these boundaries helps you design reliable
            integrations and plan for production workloads.
          </P>
          <Callout type="info">
            The targets listed on this page are engineering targets, not contractual SLAs.
            Contractual SLAs with financial backing are available with the Enterprise plan.
          </Callout>
        </Section>

        <Section id="slos" title="SLOs & Reliability Targets">
          <P>
            LogPulse tracks the following service level objectives internally and aims to meet or
            exceed these targets for all customers. These targets apply to the shared multi-tenant
            platform. Enterprise customers with dedicated infrastructure may negotiate custom SLAs.
          </P>

          <SubSection id="slo-table" title="Current Targets">
            <Table
              headers={['Metric', 'Target', 'Measurement', 'Notes']}
              rows={[
                ['Platform uptime', '99.9%', 'Rolling 30-day window', 'Excludes scheduled maintenance (announced 48h in advance)'],
                ['Ingest latency (P99)', '< 3s', 'Time from API response to queryable', 'Measured at the API gateway; does not include network transit to LogPulse'],
                ['Query latency (P95)', '< 200ms', 'Hot tier, simple queries', 'Complex aggregations or cold tier queries may exceed this target'],
                ['Backup RPO', '< 1 hour', 'Point-in-time recovery', 'Automated backups to separate availability zone'],
                ['Backup RTO', '< 4 hours', 'Full platform recovery', 'Target for complete platform restore from backup'],
                ['Alert evaluation', '< 2 minutes', 'From log ingestion to alert firing', 'Default evaluation interval; configurable per alert rule'],
              ]}
            />
            <Callout type="info">
              These are engineering targets. We publish internal performance against these targets
              monthly. Contractual SLAs with credits for missed targets are available with the
              Enterprise plan.
            </Callout>
          </SubSection>

          <SubSection id="status-page" title="Status Page">
            <P>
              The public status page is live at{' '}
              <a
                href="https://status.logpulse.io"
                className="text-primary-600 dark:text-primary-400 hover:underline"
              >
                status.logpulse.io
              </a>
              . It shows current platform health and 30 days of history per component, measured
              every minute by probes that run on Cloudflare rather than on our own platform, so
              the page keeps answering during an outage. Each check writes a log line and then
              searches for it, so what it reports is the round trip a customer would experience,
              not just whether the API is reachable.
            </P>
            <P>
              Automatic incident emails are not wired up yet, so the page is the place to look.
              To be notified by hand in the meantime, email status@logpulse.io and we will add you
              to the list for incidents and planned maintenance.
            </P>
          </SubSection>
        </Section>

        <Section id="ingest-pipeline" title="Ingest Pipeline Architecture">
          <P>
            LogPulse uses a durable, buffered ingest pipeline to ensure log data is safely received
            and stored, even during transient infrastructure issues. The pipeline is designed to
            prioritize data durability over raw throughput -- your logs are persisted in a
            Redis-backed queue before being written to ClickHouse.
          </P>
          <CodeBlock title="Ingest pipeline flow">
{`Client (HTTP/OTLP)
  |
  v
API Gateway (Fastify)
  - Authentication & rate limiting
  - Schema validation (Zod)
  - Payload size check (max 10 MB)
  |
  v
Ingest Queue (BullMQ / Redis)
  - Durable, Redis-backed persistence
  - At-least-once delivery guarantee
  - Automatic retries with exponential backoff
  |
  v
Batch Writer
  - Batches 100-1,000 events per insert
  - Flushes every 5 seconds or when batch is full
  - Parallel writers for throughput
  |
  v
ClickHouse
  - Partitioned by tenant_id + date
  - ZSTD compressed columnar storage
  - Queryable immediately after insert`}
          </CodeBlock>

          <SubSection id="delivery-semantics" title="Delivery Semantics">
            <P>
              LogPulse provides <strong>at-least-once delivery</strong>. Every log event accepted by
              the API (HTTP 202 response) is persisted in the Redis-backed ingest queue before the
              response is sent. If ClickHouse is temporarily unavailable, events remain in the queue
              and are retried automatically. In rare failure scenarios, an event may be written to
              ClickHouse more than once.
            </P>
            <Callout type="tip">
              If your application requires deduplication, include a unique identifier in the
              event attributes (e.g., <code>attributes.request_id</code>). You can then use LPQL
              to deduplicate at query time: <code>... | dedup attributes.request_id</code>.
            </Callout>
          </SubSection>

          <SubSection id="batching" title="Batching & Flushing">
            <P>
              The batch writer accumulates incoming events and writes them to ClickHouse in batches
              of 100 to 1,000 events. A flush is triggered either when the batch reaches the target
              size or after a 5-second idle timeout, whichever comes first. This balances write
              efficiency with ingestion latency -- most events become queryable within 5 seconds
              of being accepted.
            </P>
            <Table
              headers={['Parameter', 'Value', 'Description']}
              rows={[
                ['Batch size', '100 - 1,000 events', 'Adaptive based on event size and throughput'],
                ['Flush interval', '5 seconds', 'Maximum time before a partial batch is flushed'],
                ['Parallel writers', '4 per node', 'Concurrent batch writers for higher throughput'],
                ['Max queue depth', '100,000 events', 'Per-node queue limit before backpressure activates'],
              ]}
            />
          </SubSection>

          <SubSection id="failure-handling" title="Failure Handling">
            <P>
              When a batch insert to ClickHouse fails, the batch writer retries with exponential
              backoff. Failed events are retried up to 5 times with increasing delays (1s, 2s, 4s,
              8s, 16s). After all retries are exhausted, events are moved to a dead-letter queue
              (DLQ) for manual inspection and replay.
            </P>
            <Table
              headers={['Scenario', 'Behavior', 'Customer Impact']}
              rows={[
                ['ClickHouse slow (> 5s insert)', 'Queue depth grows, batch size increases', 'Slightly higher ingest latency; no data loss'],
                ['ClickHouse unavailable (< 5 min)', 'Queue buffers events, automatic retry', 'Events delayed but not lost; queries return stale results'],
                ['ClickHouse unavailable (> 5 min)', 'Queue fills; oldest events moved to DLQ', 'API returns 503; events in DLQ are replayed on recovery'],
                ['Redis unavailable', 'API returns 503 immediately', 'Client should retry with backoff; no events accepted'],
                ['Network partition', 'API returns 503 or timeout', 'Client should retry; accepted events (HTTP 202) are safe'],
              ]}
            />
            <Callout type="info">
              Dashboard-based DLQ replay is on the roadmap. Currently, DLQ events are automatically
              replayed when the underlying issue is resolved. Contact support for manual DLQ
              inspection during extended outages.
            </Callout>
          </SubSection>

          <SubSection id="backpressure" title="Backpressure & Throttling">
            <P>
              When the ingest queue approaches capacity, LogPulse applies backpressure to protect
              data durability. At 80% queue depth, a warning is logged internally. At 100% queue
              depth, the API begins returning HTTP 429 (Too Many Requests) with a Retry-After
              header indicating when to retry.
            </P>
            <P>
              Daily ingestion quotas are enforced separately from queue-based backpressure. When
              your organization reaches 80% of its daily quota, organization Owners receive an email
              notification. At 100%, ingestion is throttled and excess logs are queued for up to
              1 hour. If the quota is not increased, queued logs are discarded.
            </P>
          </SubSection>
        </Section>

        <Section id="rate-limits" title="Rate Limits & Quotas">
          <P>
            Rate limits protect the platform and ensure fair resource sharing between tenants. All
            rate limits are applied per organization and enforced at the API gateway.
          </P>

          <SubSection id="api-rate-limits" title="API Rate Limits">
            <Table
              headers={['Endpoint', 'Limit', 'Window', 'Scope']}
              rows={[
                ['POST /api/v1/logs (ingest)', '10,000 requests/min', 'Sliding window', 'Per organization'],
                ['POST /v1/logs (OTLP)', '10,000 requests/min', 'Sliding window', 'Per organization'],
                ['GET /api/v1/logs (query)', '100 requests/min', 'Sliding window', 'Per organization'],
                ['Search queries (dashboard)', '10 requests/min', 'Sliding window', 'Per organization'],
                ['Auth endpoints', '5 requests/min', 'Sliding window', 'Per IP address'],
                ['Public endpoints', '5 requests/min', 'Sliding window', 'Per IP address'],
              ]}
            />
            <P>
              When a rate limit is exceeded, the API returns HTTP 429 with the following headers:
            </P>
            <CodeBlock title="Rate limit response headers">
{`HTTP/1.1 429 Too Many Requests
Retry-After: 12
X-RateLimit-Limit: 10000
X-RateLimit-Remaining: 0
X-RateLimit-Reset: 1711929600`}
            </CodeBlock>
          </SubSection>

          <SubSection id="daily-quotas" title="Daily Ingestion Quotas">
            <Table
              headers={['Plan', 'Daily Ingestion Limit', 'Overage Behavior']}
              rows={[
                ...PLAN_LIST.filter((p) => p.id !== 'starter').map((p) => [
                  p.name,
                  formatPlanLimit(p.id),
                  p.id === 'free'
                    ? 'Ingestion paused until next day'
                    : 'Warning at 80%; throttled at 100%' + (p.id === 'business' ? '; 1h queue buffer' : ''),
                ]),
                ['Enterprise', 'Custom', 'Custom overage handling; burst allowance negotiable'],
              ]}
            />
            <Callout type="tip">
              Daily quotas reset at midnight UTC. If you consistently approach your quota limit,
              consider upgrading your plan or using pipelines to filter out low-value logs
              (debug, health checks) before they consume quota.
            </Callout>
          </SubSection>

          <SubSection id="burst-controls" title="Burst Controls">
            <P>
              Rate limits use a sliding window algorithm that allows short bursts above the
              per-minute limit. A burst of up to 2x the per-minute limit is allowed for up to
              10 seconds, as long as the rolling 1-minute average stays within the limit. This
              accommodates deployment spikes and application restarts without dropping logs.
            </P>
          </SubSection>

          <SubSection id="tenant-fairness" title="Tenant Fairness">
            <P>
              Each organization has independent rate limits and quotas. One organization's traffic
              cannot starve another organization's resources. Rate limits, query concurrency slots,
              and ingest queue capacity are allocated per tenant at the application layer. On the
              storage layer, ClickHouse queries are subject to per-query memory limits and timeouts
              that prevent any single query from monopolizing cluster resources.
            </P>
          </SubSection>
        </Section>

        <Section id="platform-limits" title="Platform Limits">
          <P>
            The following hard limits apply to all LogPulse plans. These limits are designed to
            ensure platform stability and predictable performance.
          </P>

          <SubSection id="event-limits" title="Event Size Limits">
            <Table
              headers={['Limit', 'Value', 'Notes']}
              rows={[
                ['Maximum request body', '10 MB', 'Per HTTP request; can contain multiple events'],
                ['Maximum single event', '1 MB', 'Events exceeding 1 MB are rejected with HTTP 413'],
                ['Maximum attribute count', '128 attributes', 'Per event; additional attributes are silently dropped'],
                ['Maximum attribute key length', '256 characters', 'Keys longer than 256 chars are truncated'],
                ['Maximum attribute value length', '32 KB', 'Values longer than 32 KB are truncated'],
                ['Maximum event field (body)', '256 KB', 'The event/message body field'],
              ]}
            />
          </SubSection>

          <SubSection id="query-limits" title="Query Limits">
            <Table
              headers={['Limit', 'Value', 'Notes']}
              rows={[
                ['Query timeout', '30 seconds', 'Queries exceeding 30s are cancelled and return HTTP 408'],
                ['Result set limit', '1,000 - 10,000 rows', '1,000 default; up to 10,000 with ?limit=10000'],
                ['Memory per query', '2 GB', 'Queries exceeding memory limit are cancelled'],
                ['Scan limit', '10 billion rows', 'Safety limit to prevent runaway full-table scans'],
              ]}
            />
            <Callout type="tip">
              Use time bounds and partition-aligned filters in your LPQL queries to keep scans
              efficient. For example, <code>index=main earliest=-1h</code> scans only the last
              hour of the hot tier, which is significantly faster than unbounded queries.
            </Callout>
          </SubSection>

          <SubSection id="org-limits" title="Organization Limits">
            <Table
              headers={['Resource', ...nonStarter.map((p) => p.name), 'Enterprise']}
              rows={[
                orgLimitRow('Indexes', 'indexes'),
                ['Team members', ...nonStarter.map((p) => p.maxMembers === -1 ? 'Unlimited' : String(p.maxMembers)), 'Unlimited'],
                orgLimitRow('API keys', 'apiKeys'),
                orgLimitRow('Alert rules', 'alertRules'),
                orgLimitRow('Pipelines', 'pipelines'),
                orgLimitRow('Dashboards', 'dashboards'),
                orgLimitRow('Saved queries', 'savedQueries'),
                orgLimitRow('Scheduled exports', 'scheduledExports'),
                orgLimitRow('Services', 'services'),
                orgLimitRow('KPIs', 'kpis'),
                ['Organizations per user', '5', '5', '5', '5'],
              ]}
            />
          </SubSection>
        </Section>

        <Section id="cardinality" title="Cardinality Guidance">
          <P>
            Cardinality refers to the number of distinct values a field can have. High-cardinality
            fields (like request IDs or user session tokens) can degrade query performance and
            increase storage costs if used improperly. This section provides guidance on managing
            cardinality in LogPulse.
          </P>

          <SubSection id="cardinality-best-practices" title="Best Practices">
            <Table
              headers={['Field Type', 'Cardinality', 'Recommendation']}
              rows={[
                ['source, level, index', 'Low (< 100)', 'Ideal for filtering and grouping. Use freely in attributes.'],
                ['service, environment, region', 'Low-Medium (< 1,000)', 'Good for filtering. Store as attributes.'],
                ['host, pod, container', 'Medium (< 10,000)', 'Acceptable as attributes. Avoid using in GROUP BY with large time ranges.'],
                ['user_id, account_id', 'Medium-High (< 100,000)', 'Store as attributes but avoid aggregating across all values. Use with equality filters.'],
                ['request_id, trace_id, session_id', 'Very High (> 100,000)', 'Store in the event body, not as indexed attributes. Search with full-text match.'],
                ['Full URLs, stack traces', 'Unbounded', 'Store in the event body. Never use as attribute values.'],
              ]}
            />
            <P>
              As a rule of thumb, keep indexed attributes below 100,000 distinct values per field.
              Fields exceeding this threshold should be stored in the event body and searched with
              full-text queries rather than attribute filters.
            </P>
            <Callout type="warning">
              Storing high-cardinality values (like UUIDs) as indexed attributes inflates
              ClickHouse's skip indexes and increases merge overhead. This can degrade query
              performance for all users on the same cluster. If you need to search by request ID,
              include it in the event body and use <code>event CONTAINS "req-abc123"</code>.
            </Callout>
            <CodeBlock title="Good vs. bad attribute usage">
{`// Good: low-cardinality attributes for filtering
{
  "event": "User login failed: invalid credentials for user@example.com",
  "level": "warn",
  "source": "auth-service",
  "attributes": {
    "environment": "production",
    "region": "eu-west-1",
    "error_type": "invalid_credentials"
  }
}

// Bad: high-cardinality values as attributes
{
  "event": "Request processed",
  "attributes": {
    "request_id": "550e8400-e29b-41d4-a716-446655440000",  // Too many unique values
    "full_url": "https://api.example.com/users/12345?page=3", // Unbounded
    "stack_trace": "Error: timeout at Object.<anonymous>..."   // Unbounded
  }
}`}
            </CodeBlock>
          </SubSection>
        </Section>
      </div>

      <TableOfContents items={toc} />
    </div>
  );
}
